// Section 19 and 24.13: the seeded deck (stream E2, ADR-0016). "Events are drawn in phase 8 from
// a seeded deck so that a given seed produces the same sequence regardless of player choices."
// Each season's draw is made two seasons ahead from the event stream (`ctx.eventRng`) so a warning
// can precede it, and every draw consumes the same count of numbers: for each event (in id order)
// one roll and one seed, then one roll per foreshadowable event. The chance a draw is made against
// uses only the options (base chance x season bias x Disaster frequency x Threat level x weight
// cap), never the state; `weight(ctx)` later thins what was drawn (execute.ts).
import type { Config } from '../../config';
import { seasonOfTurn } from '../../core/content-helpers';
import type { Registry } from '../../core/registry';
import type {
  EventCandidate,
  EventDraw,
  EventId,
  NewGameOptions,
  PhaseContext,
  Season,
} from '../../core/types';

const UINT32 = 4294967296;

/** Every event id in sorted order (the order the deck draws in). */
export function eventIds(registry: Registry): EventId[] {
  return (Object.keys(registry.events) as EventId[]).sort();
}

/** Events that give a one-season warning (Section 19 "half the disasters"), sorted. */
export function foreshadowIds(registry: Registry): EventId[] {
  return eventIds(registry).filter((id) => registry.events[id].foreshadow !== null);
}

/** How far `weight(ctx)` may exceed the base chance (thinning needs a ceiling). */
export function weightCap(cfg: Config, eventId: EventId): number {
  return cfg.eventDeck.weightCaps[eventId] ?? 1;
}

/**
 * Section 19 and 3: the chance an event is drawn for a season: base chance, full in its biased
 * seasons and `offSeasonMul` otherwise (D-16), times Disaster frequency, times Threat level for
 * raids and wild animals, times the event's weight cap. Options only, so decision-independent.
 */
export function deckChance(
  cfg: Config,
  registry: Registry,
  options: NewGameOptions,
  eventId: EventId,
  season: Season,
): number {
  const def = registry.events[eventId];
  const bias = def.seasons.includes(season) ? 1 : cfg.eventDeck.offSeasonMul;
  const disaster = cfg.disasterFrequency[options.disasterFrequency];
  const threat = cfg.eventDeck.threatEvents.includes(eventId)
    ? cfg.threatLevel[options.threatLevel]
    : 1;
  return Math.min(1, def.baseWeight * bias * disaster * threat * weightCap(cfg, eventId));
}

/**
 * Draw the deck executed by the events phase of `turn` (its events take effect in the season
 * after). Consumes exactly 2 x events + foreshadowable events numbers from the event stream.
 */
export function drawDeck(ctx: PhaseContext, turn: number): EventDraw {
  const season = seasonOfTurn(ctx, turn + 1);
  const candidates: EventCandidate[] = [];
  for (const eventId of eventIds(ctx.registry)) {
    const roll = ctx.eventRng.float();
    const seed = Math.floor(ctx.eventRng.float() * UINT32) >>> 0;
    const chance = deckChance(ctx.cfg, ctx.registry, ctx.state.options, eventId, season);
    if (roll < chance) candidates.push({ eventId, roll, chance, seed });
  }
  const warningRolls = foreshadowIds(ctx.registry).map(() => ctx.eventRng.float());
  return { turn, candidates, warningRolls };
}
